import React from 'react';
import { flowRight as compose, get } from 'lodash';
import PropTypes from 'prop-types';

import CustomerReviewsList from 'components/consumer/customer-reviews-list';
import CustomerReviewsHeader from 'components/consumer/customer-reviews-list/CustomerReviewHeader';
import { Helmet } from 'react-helmet-async';
import { logError } from 'commons/logger';
import withApolloSuspenseQuery from 'components/hoc/withApolloSuspenseQuery';
import { GET_ALL_REVIEWS, GET_REVIEWS_META } from '../../../mocks/queries';
import CustomerReviews from './CustomerReviews';
import withSearchState from './withSearchState';
import { REVIEWS_PAGE_SEO_DESCRIPTION } from '../../../commons/constants';

class CustomerReviewsPage extends React.Component {
  componentDidUpdate(prevProps) {
    if (prevProps.onProduct && !this.props.onProduct) {
      // Search cleared
      this.props.data
        .refetch({
          variables: {
            sortBy: this.props.sortBy,
            offset: 0,
            limit: 3,
          },
        })
        .then(async () => {
          try {
            // Temp Fix for refetch not using new variables
            await this.props.data.fetchMore({
              variables: {
                sortBy: this.props.sortBy,
                offset: 0,
                limit: 3,
              },
              updateQuery: (prevResult, { fetchMoreResult }) => ({
                allReviews: [...fetchMoreResult.allReviews],
              }),
            });
          } catch (err) {
            logError(err);
          }
        });
    }
  }

  render() {
    // To be used later when listing reviews for one product
    const { data, reviewsMetaData, config } = this.props;
    const { type, title, subTitle } = config;
    const currentReviewsCount = get(data, 'allReviews.length', 0);
    const totalReviewsCount = get(reviewsMetaData, 'reviewsMeta.count', 0);
    const averageScore = get(reviewsMetaData, 'reviewsMeta.average_score', 0);

    return (
      <>
        <Helmet>
          <meta name="description" content={REVIEWS_PAGE_SEO_DESCRIPTION} />
          {/* <link href="https://joybird.com/reviews/" rel="canonical */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Reviews by Joybird.com" />
          <meta
            property="og:description"
            content={REVIEWS_PAGE_SEO_DESCRIPTION}
          />
          <meta
            property="og:image"
            content="https://joybird2.imgix.net/meta/jb0916-FacebookPageThumbnails-Reviews-v1.jpg?auto=compress,format"
          />
          <meta property="og:url" content="https://joybird.com/reviews/" />
        </Helmet>
        <CustomerReviewsHeader
          title={title}
          subTitle={subTitle}
          type={type}
          averageScore={averageScore}
          totalReviewsCount={totalReviewsCount}
          refetch={data.refetch}
          searchState={{
            inSearchMode: this.props.inSearchMode,
            onSearchUpdate: this.props.onSearchUpdate,
            onProductClicked: this.props.onProductClicked,
            searchQuery: this.props.searchQuery,
            onProduct: this.props.onProduct,
          }}
          onSortChanged={this.props.onSortChanged}
          sortSelectOption={this.props.sortSelectOption}
        />
        {!this.props.onProduct ? (
          <CustomerReviewsList
            config={{
              type,
              reviews: data.allReviews,
              refetch: data.refetch,
              networkStatus: data.networkStatus,
              loading: data.loading,
              error: data.error,
              loadMore: async () => {
                try {
                  await data.fetchMore({
                    variables: {
                      offset: currentReviewsCount,
                      sortBy: this.props.sortBy,
                    },
                    updateQuery: (prevResults, { fetchMoreResult }) => {
                      if (!fetchMoreResult) return prevResults;
                      return {
                        allReviews: [
                          ...prevResults.allReviews,
                          ...fetchMoreResult.allReviews,
                        ],
                      };
                    },
                  });
                } catch (err) {
                  logError(err);
                }
              },
              totalReviewsCount,
              averageScore,
            }}
          />
        ) : (
          <CustomerReviews
            id={this.props.onProduct}
            productName={this.props.onProductName}
            forComponent="searchAllReviews"
            sortBy={this.props.sortBy}
            sortSelectOption={this.props.sortSelectOption}
          />
        )}
      </>
    );
  }
}

CustomerReviewsPage.propTypes = {
  data: PropTypes.shape({
    refetch: PropTypes.func,
    allReviews: PropTypes.array,
    networkStatus: PropTypes.number,
    loading: PropTypes.bool,
    error: PropTypes.string,
    fetchMore: PropTypes.func,
  }),
  onProductName: PropTypes.string,
  searchQuery: PropTypes.string,
  inSearchMode: PropTypes.bool,
  onProduct: PropTypes.number,
  onProductClicked: PropTypes.func,
  onSearchUpdate: PropTypes.func,
  onSortChanged: PropTypes.func,
  sortBy: PropTypes.string,
  sortSelectOption: PropTypes.string,
  subTitle: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  reviewsMetaData: PropTypes.object,
  config: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    subTitle: PropTypes.string,
  }),
};

export default compose(
  withApolloSuspenseQuery(GET_REVIEWS_META, { name: 'reviewsMetaData' }),
  withApolloSuspenseQuery(
    GET_ALL_REVIEWS,
    {
      options: () => ({
        variables: {
          sortBy: 'date-desc',
          offset: 0,
          limit: 3,
        },
      }),
    },
    { name: 'allReviews' }
  ),
  withSearchState
)(CustomerReviewsPage);
